import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useApp } from "@/context/AppContext";

const Navbar = () => {
  const { t, lang, setLang, currency, setCurrency } = useApp();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/", label: t("nav_home"), id: "home" },
    { to: "/properties", label: t("nav_properties"), id: "properties" },
    { to: "/about", label: t("nav_about"), id: "about" },
    { to: "/guide", label: t("nav_guide"), id: "guide" },
    { to: "/contact", label: t("nav_contact"), id: "contact" },
  ];

  const currencies = ["AED", "USD", "GBP", "INR"];

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 inset-x-0 z-50 bg-[#0A0E1A]/85 backdrop-blur-xl border-b border-[#C9A84C]/15"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="group flex items-center gap-3"
            onClick={() => setOpen(false)}
          >
            <span className="font-display text-2xl tracking-tight text-[#F5F0E8] group-hover:text-[#C9A84C] transition-colors duration-500">
              Maison
              <span className="italic text-[#C9A84C]"> Privé</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `text-sm font-light tracking-wide transition-colors duration-300 ${
                    isActive ? "text-[#C9A84C]" : "text-[#F5F0E8]/80 hover:text-[#C9A84C]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            {/* Currency */}
            <div className="relative group">
              <button
                data-testid="currency-toggle-btn"
                className="flex items-center gap-1 text-xs tracking-[0.18em] text-[#F5F0E8]/80 hover:text-[#C9A84C] transition-colors"
              >
                {currency}
                <ChevronDown className="w-3 h-3" />
              </button>
              <div className="absolute end-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                <div className="bg-[#0A0E1A] border border-[#C9A84C]/25 py-2 min-w-[80px]">
                  {currencies.map((c) => (
                    <button
                      key={c}
                      data-testid={`currency-option-${c}`}
                      onClick={() => setCurrency(c)}
                      className={`block w-full text-start px-4 py-1.5 text-xs tracking-wider transition-colors ${
                        currency === c
                          ? "text-[#C9A84C]"
                          : "text-[#F5F0E8]/70 hover:text-[#C9A84C]"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <span className="w-px h-4 bg-[#C9A84C]/20" />

            {/* Language */}
            <button
              data-testid="language-toggle-btn"
              onClick={() => setLang(lang === "en" ? "ar" : "en")}
              className="flex items-center gap-2 text-xs tracking-[0.18em] text-[#F5F0E8]/80 hover:text-[#C9A84C] transition-colors"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === "en" ? "العربية" : "English"}
            </button>

            <Link
              to="/contact"
              data-testid="nav-book-cta"
              className="btn-gold px-6 py-2.5 text-xs tracking-[0.18em] uppercase font-medium"
            >
              {t("nav_book")}
            </Link>
          </div>

          {/* Mobile menu */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden text-[#F5F0E8]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden border-t border-[#C9A84C]/15 bg-[#0A0E1A]"
        >
          <div className="px-6 py-8 space-y-6">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.id}`}
                className={({ isActive }) =>
                  `block font-display text-2xl ${
                    isActive ? "text-[#C9A84C]" : "text-[#F5F0E8]/85"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="gold-divider my-4" />
            <div className="flex items-center justify-between">
              <button
                onClick={() => setLang(lang === "en" ? "ar" : "en")}
                data-testid="mobile-language-toggle"
                className="flex items-center gap-2 text-sm text-[#F5F0E8]/85"
              >
                <Globe className="w-4 h-4" /> {lang === "en" ? "العربية" : "English"}
              </button>
              <div className="flex gap-3">
                {currencies.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCurrency(c)}
                    data-testid={`mobile-currency-${c}`}
                    className={`text-xs tracking-wider ${
                      currency === c ? "text-[#C9A84C]" : "text-[#F5F0E8]/60"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-book-cta"
              className="btn-gold block text-center px-6 py-3 text-xs tracking-[0.18em] uppercase"
            >
              {t("nav_book")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;